import React, { useState, memo, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './PriceComponentList.module.scss';
import classNames from 'classnames/bind';
import { BaseTable } from '~/components/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { priceComponentActions } from '~/thunks/price-components/priceComponentsSlice';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import MenuOption from '~/components/price-components/menu-option/MenuOption';
import { IPriceComponent, IPriceComponentList } from '~/utils/interface/estimate';
import { useTranslation } from 'react-i18next';
import { formatDate } from '~/utils/helper';

const cx = classNames.bind(styles);

type PriceComponentListProps = {
  data?: IPriceComponent;
  onOpenEdit: (id: string) => void;
};

function PriceComponentList(props: PriceComponentListProps) {
  const { t } = useTranslation(['admin/price-component']);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'name',
        label: t('componentName'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'createdAt',
        label: t('createdAt'),
        sortable: true,
        searchable: false,
      },
    ],
    [t]
  );

  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  const { priceComponents, filter, currentPage, totalPages } = useAppSelector((state) => state.priceComponent);
  const dispatch = useAppDispatch();

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    dispatch(priceComponentActions.changePriceComponentId(itemId));
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(itemId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(priceComponentActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(priceComponentActions.changeMenuSelectId(menuSelectedId));
      dispatch(priceComponentActions.changeOpenAlertDelete(true));
    }
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      priceComponentActions.setFilter({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      priceComponentActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };

  return (
    <Box className={cx('price-component-list-container')}>
      <BaseTable<IPriceComponentList>
        data={priceComponents}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        renderCell={(priceComponent: IPriceComponentList) => {
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('price-component-container')}>
                  <Box className={cx('priceComponentName')}>{priceComponent.name}</Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box className={cx('createdAt-container')}>
                  <Box className={cx('priceComponentName')}>
                    {' '}
                    {priceComponent.createdAt ? formatDate(priceComponent.createdAt) : 'N/A'}
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <IconButton
                  onClick={(event) => {
                    openOptionMenu(event, priceComponent.id);
                  }}
                >
                  <MenuVerticalIcon className='w-[19px] h-[19px]' />
                </IconButton>
              </TableCell>
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
      />
    </Box>
  );
}

export default memo(PriceComponentList);
